import { Injectable } from '@angular/core';
import { LoadingProvider } from '../loading/loading';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { RecipientProvider } from '../recipient/recipient';
import { RouteProvider } from '../route/route';
import { PaymentProvider } from '../payment/payment';
import { CustomerProvider } from '../customer/customer';
import { IOrder } from '../../model/models/order/order-interface';
import { IAgent } from '../../model/models/agent/agent.interface';
import { CustomerIdentificationStatusType } from '../../model/types/customer-identification-status-type';
import { ResourceProvider } from '../resource/resource';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { APP_CONFIG, Region } from '../../environment';
import { IdentityVerificationProvider } from '../identity-verification/identity-verification';
import { IdologyStatusType } from '../../model/types/idology-status-type';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { TranslateService } from '@ngx-translate/core';
import { ErrorCodeType } from '../../model/types/error-code-type';
import { CustomersProvider } from '../myria/customer-provider/customers-provider';
import { RecipientMapper } from '../../providers/recipient/recipient.mapper';
import { PAGES, RET_VAL, CUSTOMER_STATUS, OTP_ACTIONS, SEND_REGARDLESS_ONHOLD, NOT_SEND_REGARDLESS_ONHOLD, MOBILE_WALLET_FIELDID, TRANSFER_REASON_FIELDID, DELIVERY_METHOD} from '../../providers/myria/app-variables';
import { FormControlMapper } from '../../providers/formbuilder/formcontrol-mapper';
import { FormControlProvider } from '../../providers/formbuilder/formcontrol';
import { ModalController, Platform } from 'ionic-angular';
import { OrderProvider } from '../../providers/order/order';
import { UtilsProvider } from '../myria/utils-provider';
import { FeatureFlagTypes } from '../../model/types/feature-flag-types';
import { BridgeBankDepositEntry, BridgeBankDepositCompleteData } from '../../model/types/bridge-types';
import { AlertProvider } from '../../providers/myria/alert-provider';
import { PaymentMethodType } from '../../model/types/payment-method-type';
import { AnalyticsEventType, AnalyticsPaymentMethodData, AnalyticsPayoutMethodType, PayoutAgentType, SegmentLocationNames, PasscodeChallengeLocation } from '../../model/types/analytics.const';
import { SegmentProvider } from '../analytics/segment';
import { PinActionType } from '../../model/types/wallet-pin-action-type';
import { VpaFormProvider } from '../vpa-form/vpa-form';
import { LoginProvider } from '../../providers/myria/login-provider';
import { AgentProvider } from '../agent/agent';
import { HttpProvider } from '../http/http';
import { ICustomer } from '../../model/models/customer'
import { ICustomerIdentificationProcessor } from '../../model/models/customer/customer.identification.interface';
import { CustomerIdentificationProcessorType } from '../../model/types/customer-identification-processor-type';
import { MobileWalletProvider } from '../mobile-wallet/mobile-wallet-provider';
import { BankDepositSmartRoutingProvider } from '../bank-deposit-smart-routing/bank-deposit-smart-routing';
import { IBankDepositSmartRoutingProvider } from '../../model/models/agent/bank-deposit-provider.interface';
import { BranchProvider } from '../analytics/branch';
import { FirebaseAnalyticsProvider } from '../analytics/firebase-analytics';
import { getCampaignAnalyticsData, getSourceAnalyticsData } from '../../providers/analytics/deeplink-source';
import { PlaidProvider } from '../../providers/plaid/plaid';
import { PriceCalculatorProvider } from '../price-calculator/price-calculator';
import { Transaction, TransactionViewType } from '../../model/myria/app/transaction';
import { MissingTransactionInfoType } from '../../model/types/missing-transaction-info-type';
import { BankAccountAddAllowed, BankAccountVerificationStatus } from '../../model/types/bank-account-verification-status';
import { NewPaymentErrorType } from '../../model/types/newPayment-error-type';
import { NewPaymentErrorDescription } from '../../model/types/newPayment-error-description';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { regions } from '../../environment/region/regions';
import { DocType } from '../../model/types/doc-type';
import { AdditionalDetailsProvider } from '../../providers/send-flow/additional-details';
import { OnfidoProvider } from '../../providers/myria/onfido-provider';
import { isCurrencyAllowedForCustomer, showStablecoinUnavailableModal } from '../../utils/stablecoin-mvp';
import { SendOpenBankingProvider } from '../openbanking/send-openbanking';
import { ForeignIpGeolocationSession } from '../foreign-ip-geolocation/foreign-ip-geolocation-session';
@Injectable()
export class SendFlowProvider {
  lastRecipientPage;
  lastPaymentPage;
  customerFields;
  recipientFields;
  recipient = null;
  mobileAccountValidations = null;
  analyticsLocation: string;
  sendAgain = false;
  private pendingWalletType: string | null = null;

  constructor(private loading: LoadingProvider,
              private routeProvider: RouteProvider,
              private customerProvider: CustomerProvider,
              private recipientProvider: RecipientProvider,
              private paymentProvider: PaymentProvider,
              private identityProvider: IdentityVerificationProvider,
              private translate: TranslateService,
              private resourceProvider: ResourceProvider,
              private customersProvider: CustomersProvider,
              public formControlProvider: FormControlProvider,
              private vpaFormProvider: VpaFormProvider,
              public modalCtrl: ModalController,
              private platform: Platform,
              private orderProvider: OrderProvider,
              private alert: AlertProvider,
              private utils: UtilsProvider,
              private analytics: SegmentProvider,
              private walletProvider: MobileWalletProvider,
              private bankDepositSmartRoutingProvider: BankDepositSmartRoutingProvider,
              private branchProvider: BranchProvider,
              private firebaseAnalyticsProvider: FirebaseAnalyticsProvider,
              private plaidProvider: PlaidProvider,
              private calcProvider: PriceCalculatorProvider,
              private loginProvider: LoginProvider,
              private agentProvider: AgentProvider,
              private storageProvider: StorageProvider,
              private additionalDetailsProvider: AdditionalDetailsProvider,
              private sendOpenBankingProvider: SendOpenBankingProvider,
            ) {
  }

  logout(order = null) {
    this.lastRecipientPage = null;
    this.lastPaymentPage = null;
    this.orderProvider.setCurrentOrder(order);
  }

  async startFlow(order: IOrder, sendFromRecipient: boolean = false, identityVerified: boolean = false, isSendAgain: boolean = false, options: { openRecipientModal?: boolean; showLoading?: boolean } = {}) {
    this.orderProvider.allowCheckKycInfoOverride = false;
    this.logout(order);
    ForeignIpGeolocationSession.resetForNewFlow();
    const showLoading = options.showLoading !== false;
    const openRecipientModal = options.openRecipientModal === true;
    if (showLoading)
      this.loading.showDelayed();
    order.orderStarted = true;
    this.setDifferentLocationForAnalytics();

    if ((sendFromRecipient || isSendAgain) && order.recipient) {
      if (order.recipient.country !== order.countryTo) {
        LoggerProvider.info(this, `Recipient country ${order.recipient.country} does not match order country ${order.countryTo}`);
        
        this.addRecipient(false, null, false, order.recipient, true);
      } else
      await this.selectedRecipient(order.recipient, false);
    } else if (openRecipientModal) {
      await this.selectRecipientModal(identityVerified, showLoading);
    } else {
      await this.selectRecipient(identityVerified);
    }

    if (showLoading)
      this.loading.hide();
  }
  async requireIdentityVerification(order: IOrder, customer: ICustomer) {
    // if the amount or the combined limit is higher than a threshold, Veriff/Onfido is required
    // US check probably not needed but better be safe than sorry
    if (APP_CONFIG.region.region === Region.US && customer) {
      // if Veriff/Onfido has been done we don't need an identity verification anymore
      const onfidoStatus = await this.customerProvider.getIdentityVerificationFromCustomer([CustomerIdentificationProcessorType.Veriff, CustomerIdentificationProcessorType.Onfido]);
      if (onfidoStatus)
        return null;

      // for the states OK and NM Veriff/Onfido is always required when it goes above 1000
      if ((order.amountFrom >= 3000.0 || (customer.identification && customer.identification.isHighLimitIdentityVerificationRequired)) || (order.amountFrom >= 1000.0 && customer.address && customer.address.stateProvince && (customer.address.stateProvince === 'OK' || customer.address.stateProvince === 'NM')))
        return CustomerIdentificationProcessorType.Veriff; // We will decide on Veriff/Onfido through country settings HIGHER_LIMIT

    }

    return null;
  }

  async selectRecipient(replacePage: boolean = false, onContinue = null) {
    let recipients = null;
    if (this.recipientProvider.recipients)
      recipients = this.recipientProvider.recipients;
    else {
      this.loading.showDelayed();
      try {
        recipients = await this.recipientProvider.getRecipients();
      }
      catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get recipients in send flow provider');
      }
      this.loading.hide();
    }

    if (!recipients)
      return;

    if (this.routeProvider.getReplaceOrigPage())
      replacePage = true;

    // const order = this.orderProvider.getCurrentOrder();
    // const visibleRecipients = recipients.filter(recipient => {
    //   if(recipient.country)
    //      return recipient.country.toLowerCase() === order.countryTo.toLowerCase();
    //   return false;
    // });

    const visibleRecipients = this.filterCountryWiseRecipients(recipients)
    if (visibleRecipients.length === 0)
      this.addRecipient(replacePage);
    else {
      if (replacePage)
        this.routeProvider.replace(this.routeProvider.pages.SendToRecipientsPage, this.getSendToRecipientsParams(visibleRecipients, onContinue));
      else
        this.routeProvider.push(this.routeProvider.pages.SendToRecipientsPage, this.getSendToRecipientsParams(visibleRecipients, onContinue));
    }
    this.lastRecipientPage = this.routeProvider.getLastPushedPage();
  }

  filterCountryWiseRecipients(recipients){

    const order = this.orderProvider.getCurrentOrder();
    const visibleRecipients = recipients.filter(recipient => {
      if(recipient.country)
         return recipient.country.toLowerCase() === order.countryTo.toLowerCase();
      return false;
    });

    return visibleRecipients;

  }

  async selectRecipientModal(replacePage: boolean = false, onContinue = null, showLoading: boolean = true) {
    let recipients = null;
    if (this.recipientProvider.recipients)
      recipients = this.recipientProvider.recipients;
    else {
      if (showLoading)
        this.loading.showDelayed();
      try {
        recipients = await this.recipientProvider.getRecipients();
      }
      catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get recipients in send flow provider');
      }
      if (showLoading)
        this.loading.hide();
    }

    if (!recipients)
      return;

    if (this.routeProvider.getReplaceOrigPage())
      replacePage = true;

    const visibleRecipients = this.filterCountryWiseRecipients(recipients);
    if (visibleRecipients.length === 0)
      await this.openAddRecipientModal(onContinue);
    else
      await this.routeProvider.openModal(this.routeProvider.pages.SendToRecipientsPage, this.getSendToRecipientsParams(visibleRecipients, onContinue));

    this.lastRecipientPage = this.routeProvider.getLastPushedPage();
  }

  async openAddRecipientModal(onContinue = null, fromSendAgain?, copyRecipient?, countryMissMatch = false) {
    if (countryMissMatch) {
      this.routeProvider.openActionSheet(
        this.routeProvider.pages.ModalAlertPage, {
          title: this.translate.instant('page.recipientCountryMismatch.title'),
          subtitle: this.translate.instant('page.recipientCountryMismatch.description'),
          image: 'assets/icons/identity_card.svg',
          primaryButtonText: this.translate.instant('global.genericContinueButton'),
          showSecondaryButton: false
        }
      )
    }
    const order = this.orderProvider.getCurrentOrder();
    const payoutMethod = order ? this.orderProvider.getAnalyticsDeliveryMethodType(order.deliveryMethodInternal): null;
    this.analytics.trackEvent({ eventName: AnalyticsEventType.RecipientCreationStarted, analyticsData: { location: SegmentLocationNames.SendMoney, payoutMethod: payoutMethod && payoutMethod == AnalyticsPaymentMethodData.BankAccount? "Bank" : payoutMethod} });

    const params = {
      country: order.countryTo,
      deliveryMethod: order.deliveryMethodInternal,
      amount: order.amountTo,
      amountFrom: order.amountFrom,
      currency: order.currencyTo,
      sendAmount: order.amountFrom,
      promoRate: order.exchangeRatePromo,
      fromSendAgain: fromSendAgain,
      copyRecipient: copyRecipient,
      onCreated: (recipient, location, agentLocation, fromBank?) => {
        this.recipientProvider.getRecipients(true); // reload recipients to include new recipient
        if((order.paymentMethod == PaymentMethodType.StagedRia || order.paymentMethod == PaymentMethodType.SencillitoOnline || order.paymentMethod == PaymentMethodType.PayID || order.paymentMethod == PaymentMethodType.StagedOnline) && fromBank) {
          agentLocation = {agentId: location, locationId: agentLocation}
        }
        this.selectedRecipient(recipient, true, this.analyticsLocation, location, agentLocation, onContinue, fromBank);
      }
    };
    await this.routeProvider.openModal(this.routeProvider.pages.SendToNewRecipientPage, params);
  }

  getSendToRecipientsParams(recipients = null, onContinue = null) {
    const order = this.orderProvider.getCurrentOrder();
    return {
      recipients: recipients,
      country: order.countryTo,
      order: order,
      deliveryMethod: order.deliveryMethodInternal,
      promoRate: order.exchangeRatePromo,
      onAdd: () => { this.addRecipient(false, onContinue); },
      onSelect: (recipient) => { this.selectedRecipient(recipient, false, this.analyticsLocation, null, null, onContinue); }
    };
  }

  setDifferentLocationForAnalytics() {
    if(this.orderProvider.getCurrentOrder().queueStatus) {
      this.analyticsLocation = SegmentLocationNames.SendAgain;
    }
    else {
      this.analyticsLocation = SegmentLocationNames.SendMoney;
    }
  }

  addRecipient(replacePage: boolean = false, onContinue = null, fromSendAgain?, copyRecipient?, countryMissMatch = false) {
    if (countryMissMatch) {
      this.routeProvider.openActionSheet(
        this.routeProvider.pages.ModalAlertPage, {
          title: this.translate.instant('page.recipientCountryMismatch.title'),
          subtitle: this.translate.instant('page.recipientCountryMismatch.description'),
          image: 'assets/icons/identity_card.svg',
          primaryButtonText: this.translate.instant('global.genericContinueButton'),
          showSecondaryButton: false
        }
      )
    }
    const order = this.orderProvider.getCurrentOrder();
    const payoutMethod  =  order ? this.orderProvider.getAnalyticsDeliveryMethodType(order.deliveryMethodInternal): null ;
    this.analytics.trackEvent({ eventName: AnalyticsEventType.RecipientCreationStarted, analyticsData: { location: SegmentLocationNames.SendMoney,payoutMethod : payoutMethod && payoutMethod == AnalyticsPaymentMethodData.BankAccount? "Bank" : payoutMethod} });

    const params = {
      country: order.countryTo,
      deliveryMethod: order.deliveryMethodInternal,
      amount: order.amountTo,
      amountFrom: order.amountFrom,
      currency: order.currencyTo,
      sendAmount: order.amountFrom,
      promoRate: order.exchangeRatePromo,
      fromSendAgain: fromSendAgain,
      copyRecipient: copyRecipient,
      onCreated: (recipient, location, agentLocation, fromBank?) => {
        this.recipientProvider.getRecipients(true); // reload recipients to include new recipient
        if((order.paymentMethod == PaymentMethodType.StagedRia || order.paymentMethod == PaymentMethodType.SencillitoOnline || order.paymentMethod == PaymentMethodType.PayID || order.paymentMethod == PaymentMethodType.StagedOnline) && fromBank) {
          agentLocation = {agentId: location, locationId: agentLocation}
        }
        this.selectedRecipient(recipient, true, this.analyticsLocation, location, agentLocation, onContinue, fromBank);
      }
    };
    if (replacePage)
      this.routeProvider.replace(this.routeProvider.pages.SendToNewRecipientPage, params);
    else
      this.routeProvider.push(this.routeProvider.pages.SendToNewRecipientPage, params);
  }

  // edit the existing recipient from Recipient Details (pre-filled), then continue as a normal SMF 
  editRecipientAndSend(recipient) {
    const order = this.orderProvider.getCurrentOrder();
    const params = {
      recipient,
      enableEdit: true,
      canEditRecipient: true,
      country: order.countryTo,
      deliveryMethod: order.deliveryMethodInternal,
      amount: order.amountTo,
      amountFrom: order.amountFrom,
      currency: order.currencyTo,
      sendAmount: order.amountFrom,
      promoRate: order.exchangeRatePromo,
      onCreated: (recipient, location, agentLocation, fromBank?) => {
        this.recipientProvider.getRecipients(true);
        if ((order.paymentMethod == PaymentMethodType.StagedRia || order.paymentMethod == PaymentMethodType.SencillitoOnline || order.paymentMethod == PaymentMethodType.PayID || order.paymentMethod == PaymentMethodType.StagedOnline) && fromBank) {
          agentLocation = { agentId: location, locationId: agentLocation };
        }
        this.selectedRecipient(recipient, true, this.analyticsLocation, location, agentLocation, null, fromBank);
      }
    };
    this.routeProvider.push(this.routeProvider.pages.SendToNewRecipientPage, params);
  }

  async addAccount() {
    const order = this.orderProvider.getCurrentOrder();
    const params = {
      paymentMethod: order.paymentMethod,
      cardType: order.paymentMethod,
      location: 'sendMoney',
      onCreated: (account) => { this.selectedPaymentAccount(account, true); }
    };
    if (APP_CONFIG.region.isPlaidEnabled && order.paymentMethod === PaymentMethodType.BankAccount) {
      this.loading.show();
      if (await this.paymentProvider.isBankAccountAddAllowed() == BankAccountAddAllowed.Allowed) {
        this.plaidProvider.plaidNative(params);
      }
      else if (await this.paymentProvider.isBankAccountAddAllowed() == BankAccountAddAllowed.MaxUnverifiedBankAccountsReached){
        let params = {
          location: SegmentLocationNames.SendMoney,
          paymentMethod: PaymentMethodType.BankAccount,
          paymentVendor:order.paymentMethod,
          errorType: NewPaymentErrorType.BankValidationFailed,
          errorDescription: NewPaymentErrorDescription.MaximumCardReached,
          bankVerificationMethod: null,
          bankVerificationResult: null,
          institutionId: null,
          institutionName: null
        };
        this.showMaxBankAccountsError('page.paymentAccountListPage.maximumUnverifiedBankAccountsContent');
      } else {
        this.showMaxBankAccountsError('page.paymentAccountListPage.maxiumBankAccountsContent');
      }
      this.loading.hide();
    }
    // this.routeProvider.openModal(this.routeProvider.pages.BankAccountVerificationPage, params);
    else
      this.routeProvider.openModal(this.routeProvider.pages.AddCardPage, params);
  }

    showMaxBankAccountsError(subtitleKey: string = '') {
    let image = "assets/imgs/error_emoji.svg";
    let imageWithCircle = true;
    let title = this.translate.instant('page.paymentMethods.bank.maxAccountLimitReached.header');
    let subtitle = this.translate.instant('page.paymentMethods.bank.maxAccountLimitReached.description');
    let primaryButtonText = this.translate.instant('global.okayButton');
    let secondaryButtonText = null;
    let showSecondaryButton = false;
    let secondaryAction = null;
    let primaryAction = null;

    if (APP_CONFIG.region.defaultCountryFrom === 'US') {
      title = this.translate.instant('page.paymentAccountListPage.maximumBankAccountsHeader');
      subtitle = this.translate.instant(subtitleKey);
      primaryButtonText = this.translate.instant('page.paymentAccountListPage.chooseAnotherBankButton');
      secondaryButtonText = this.translate.instant('page.paymentAccountListPage.goToProfileButton');
      showSecondaryButton = this.routeProvider.hasPage(this.routeProvider.pages.CheckPricesPage) && !this.routeProvider.hasPage(this.routeProvider.pages.SettingsPage);
      secondaryAction = () => this.routeProvider.push(this.routeProvider.pages.SettingsPage);
      image = "assets/imgs/modal-error.svg";
      imageWithCircle = false;
      primaryAction = null;
    }
    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: image,
        imageWithCircle: imageWithCircle,
        title: title,
        subtitle: subtitle,
        primaryButtonText: primaryButtonText,
        showPrimaryButton: true,
        secondaryButtonText: secondaryButtonText,
        showSecondaryButton: showSecondaryButton,
        secondaryAction: secondaryAction,
        primaryAction: primaryAction
      },
      null,
      'bank-verification-pending-modal'
    );
  }

  async selectedRecipient(recipient, newRecipient, analyticsLocation = null, location = null, agentLocation = location, onContinue = null, fromBank: boolean = false) {
    let replacePage = false;
    if (this.routeProvider.getReplaceOrigPage())
      replacePage = true;

    const order = this.orderProvider.getCurrentOrder();
    /* Do not fire recipient selected event if selectedRecipient is called after selecting a payout location or analyticsLocation is undefined */

    if (order.recipient && order.recipient.id !== recipient.id) {
      order.agentTo = null;
      order.agentToLocation = null;
    }
    order.recipient = recipient;

    if(newRecipient && fromBank) {
      this.logRecipientBankDetails(recipient);
    }

    if (location) {
      order.agentTo = location;
      if (order.agentTo.agentLocation)
        order.agentToLocation = order.agentTo.agentLocation;
    }
    if (agentLocation)
      order.agentToLocation = agentLocation;
    this.orderProvider.setCurrentOrder(order);

    const requireLocation = await this.orderProvider.requireLocationSelection(order);
    let cities = [];
    let payload;
    let searchCity = "";
    if (requireLocation && !newRecipient) {
      let cityUnusable = false;
      try {
        payload = {
          countryTo: order.countryTo,
          city: order.recipient.city,
          deliveryMethod: order.deliveryMethodInternal
        }
        this.loading.show();
        cities = await this.agentProvider.getAgentCities(payload);   
        this.loading.hide();
        if (cities != null && cities.length > 1) {
          const match = this.agentProvider.findMatchingCity(cities, order.recipient.city, order.recipient.state);
          if (match)
            cities = [ match ];
          else
            searchCity = order.recipient.city + ", " + order.recipient.state;
        } else 
          searchCity = "";
        cityUnusable = !cities || cities.length === 0;
      }
      catch (ex) {
        this.loading.hide();
    	  cities = null;
        // 404 means the recipient's city matches no payout city
        if (ex && ex.status === 404)
          cityUnusable = true;
        else
          LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to get agent cities in search city component');
      } 

      if (cityUnusable) {
        this.showRecipientCityUnusableError(order);
        return;
      }
      
      let tempSelectCity;
      const params = {
        sendAmount: order.amountFrom,
        amount: order.amountTo,
        currency: order.currencyTo,
        country: order.countryTo,
        promoRate: order.exchangeRatePromo,
        deliveryMethod: order.deliveryMethodInternal,
        searchCity: searchCity,
        city: tempSelectCity? tempSelectCity: cities && cities.length === 1 ? cities[0] : cities,
        showLoader: false,
        onPreSelect: (location) => { this.preSelectedLocation(location); },
        onSelect: (location, agentlocation) => { this.selectedLocation(location, agentlocation, onContinue); }
      };
      
      this.routeProvider.pushOrReplace(replacePage, this.routeProvider.pages.SendToLocationPage, params, null, false);
    }
    else if ((order.deliveryMethodInternal === DeliveryMethodType.BankDeposit
      || order.deliveryMethodInternal === DeliveryMethodType.PagoMovil) &&
      (!newRecipient || (!fromBank && this.isRmt5BankDepositEnabled()))) {
      if (newRecipient && this.routeProvider.hasPage(this.routeProvider.pages.SendToRecipientsPage)) {
        this.routeProvider.popTo(this.routeProvider.pages.SendToRecipientsPage);
      }
      await this.showSelectBankDeposit(order, false, newRecipient, newRecipient ? onContinue : null);
    }
    else if (order.deliveryMethodInternal === DeliveryMethodType.DebitCard && !newRecipient) {
      this.showSelectDebitCardPayout(order,false);
    }
    else if (this.vpaFormProvider.isVpaFormDeliveryMethod(order.deliveryMethodInternal)) {
      const deliveryMethod = order.deliveryMethodInternal;
      this.loading.show();
      try {
        const recipientFields = await this.vpaFormProvider.getRecipientFields(order, deliveryMethod);
        if (recipientFields) {
          const params = {
            recipientFields: recipientFields,
            recipient: recipient,
            order: this.orderProvider.getCurrentOrder(),
            onContinue: (data) => {
              this.vpaFormProvider.applyFormData(order, deliveryMethod, data);
              this.orderProvider.setCurrentOrder(order);
              this.finalizeRecipient(newRecipient, onContinue);
            }
          };
          this.routeProvider.pushOrReplace(replacePage, this.routeProvider.pages.VpaFormPage, params);
          this.loading.hide();
        }
      } catch (ex) {
        this.loading.hide();
        if (ex && ex.errorMessage)
          RmtPopupComponent.showException(this, ex, ex.errorMessage);
        else {
          RmtPopupComponent.showExceptionWithLogTranslation(this, ex, 
            'global.errorGeneralMessage', 
            'global.errorGeneralHeader', null);
        }
      }
    }
    else if (order.deliveryMethodInternal === DeliveryMethodType.MobilePayment) {
      await this.handleMobileWalletAndRiaWallet(order, newRecipient, replacePage)
    }
    else
      await this.finalizeRecipient(newRecipient, onContinue);
  }

  /** Resolves the corridor gate before the bank screen is pushed, so it can be read synchronously. */
  private async resolveBankDepositSmartRouting(order: IOrder): Promise<void> {
    try {
      await this.bankDepositSmartRoutingProvider.ensureSmartRoutingEnabledForOrder(order);
    }
    catch (ex) {
      // The bank screen has to open either way; an unresolved gate only leaves it on the legacy engine.
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to resolve the bank deposit smart routing gate, continuing on the legacy engine');
    }
  }

  selectWallet(order, walletId, newRecipient, replace = false, selectedWalletNumber = null) {
    this.routeProvider.pushOrReplace(replace, this.routeProvider.pages.MobileWalletDetails, {
      order: order,
      selectedWalletId: walletId,
      selectedWalletNumber,
      onContinue: (order) => {
        this.orderProvider.setCurrentOrder(order);
        this.finalizeRecipient(newRecipient);
      }
    });
  }

  fireRecipientSelectedEvent(recipient, newRecipient = false, analyticsLocation, lineNumber: number | null = null) {
      const order = this.orderProvider.getCurrentOrder();
      let analyticsDeliveryMethod = null;
      if(!analyticsLocation)
        analyticsLocation = SegmentLocationNames.SendMoney

      if(analyticsLocation === SegmentLocationNames.Recipients || analyticsLocation === SegmentLocationNames.HomeScreen) {
        analyticsDeliveryMethod = this.orderProvider.getAnalyticsDeliveryMethodType(recipient.lastDeliveryMethod)
      } else if (order) {
        analyticsDeliveryMethod = this.orderProvider.getAnalyticsDeliveryMethodType(order.deliveryMethodInternal);
      }

      const analyticsData = {
          recipientId: SegmentProvider.formatInt(recipient.id),
          justCreated: newRecipient ? "True" : "False",
          destinationCountry: recipient.country,
          payoutMethod: analyticsDeliveryMethod,
          location: analyticsLocation,
          ...(analyticsDeliveryMethod === DeliveryMethodType.PagoMovil && { payoutAgentName: DeliveryMethodType.PagoMovil }),
          ...(lineNumber != null && { lineNumber }),
      };

      this.analytics.trackEvent({
          eventName: AnalyticsEventType.RecipientSelected,
          analyticsData,
      });
  }

  // TODO copy: placeholder keys - swap for the proposed strings on OM-20871 once created in Lokalise
  private showRecipientCityUnusableError(order: IOrder) {
    LoggerProvider.warning(this, LoggerEvent.ComponentException,
      'Recipient city resolves to no payout city, prompting the customer to update the recipient',
      { countryTo: order.countryTo, deliveryMethod: order.deliveryMethodInternal });

    const imagePath = 'assets/imgs/push-pin-icon.svg';
    const title = this.translate.instant('page.errorMessage.ria_errormessage_agentlocationnotavailableheader');
    const msg = this.translate.instant('page.errorMessage.ria_errormessage_recipientnotavailablebody');
    const primaryButtonText = this.translate.instant('page.errorMessage.ria_errormessage_updaterecipientbutton');

    this.orderProvider.showGenericErrorModal(imagePath, title, msg, primaryButtonText, () => this.editRecipientForCity(order));
  }

  private async editRecipientForCity(order: IOrder) {
    const customer = await this.customerProvider.getCustomer();
    const canEditRecipient = customer && customer.kycStatus && customer.kycStatus.canEditRecipient
      ? customer.kycStatus.canEditRecipient
      : true;

    this.routeProvider.openModal(this.routeProvider.pages.RecipientDetailsPage, {
      recipient: order.recipient,
      amount: order.amountTo,
      currency: order.currencyTo,
      canEditRecipient,
      isFromRecipients: true,
      enableEdit: true
    });
  }

  // checks if the recipient has his bankAgentId and/or bankAgentLocationId and fetch then if needed
  async finalizeRecipient(newRecipient, onContinue = null) {
    const order = this.orderProvider.getCurrentOrder();
    this.clearBankDepositRoutingOffBankDeposit(order);
    if (this.orderProvider.isBankDepositDeliveryMethod(order.deliveryMethodInternal)) {
      this.loading.showDelayed();
      await this.resolveBankDepositSmartRoutingForRecipient(order);
      try {
        const savedRecipient = await this.recipientProvider.getRecipient(order.recipient.id);
        order.recipient = savedRecipient;
        this.orderProvider.setCurrentOrder(order);
      }
      catch (ex) {
        this.loading.hide();
        RmtPopupComponent.showExceptionWithLogTranslation(this, ex, "page.errorPage.bankAccountError");
        return;
      }
      order.agentToLocation = { agentId: order.recipient.bankAgentId, locationId: order.recipient.bankAgentLocationId };
      this.applyBankDepositSmartRouting(order);
    }
    else if (order.deliveryMethodInternal==DeliveryMethodType.OfficePickup || 
      order.deliveryMethodInternal==DeliveryMethodType.HomeDelivery){
      this.loading.showDelayed();
      try {
        const savedRecipient = await this.recipientProvider.getRecipient(order.recipient.id);
        savedRecipient.city = order.agentTo && order.agentTo.agentCity ? order.agentTo.agentCity.cityName : '';
        savedRecipient.state = order.agentTo && order.agentTo.agentCity ? order.agentTo.agentCity.stateAbbreviation : '';
        order.recipient = savedRecipient;
        this.orderProvider.setCurrentOrder(order);
        this.recipientProvider.updateRecipient(savedRecipient);
      }
      catch (ex) {
        this.loading.hide();
        RmtPopupComponent.showExceptionWithLogTranslation(this, ex, "page.errorPage.bankAccountError");
        return;
      }
    }

    order.selectedDelivery = true;
    this.orderProvider.setCurrentOrder(order);

    if (onContinue)
      onContinue()
    else
      await this.selectPaymentAccount(newRecipient, false);
  }

  /**
   * Only a bank deposit carries a resolved payout provider, and no delivery method switch drops one, so
   * a switch away from a bank deposit has to be caught here or its routing ids ride on the next order.
   * isSmartRouting is deliberately left alone: the wallet flow owns it for a mobile payment.
   */
  private clearBankDepositRoutingOffBankDeposit(order: IOrder) {
    if (order.deliveryMethodInternal !== DeliveryMethodType.BankDeposit)
      order.bankDepositProvider = null;
  }

  private async resolveBankDepositSmartRoutingForRecipient(order: IOrder): Promise<void> {
    if (order.deliveryMethodInternal !== DeliveryMethodType.BankDeposit
      || this.bankDepositSmartRoutingProvider.isSmartRoutedTransaction)
      return;
    await this.resolveBankDepositSmartRouting(order);
  }

  /** Points the order at the smart-routed correspondent, or leaves the recipient's saved payout agent. */
  private applyBankDepositSmartRouting(order: IOrder) {
    // finalizeRecipient treats several delivery methods as bank deposit; only BankDeposit is routed.
    // The gate is resolvable from the standalone bank screens too, so the resolved corridor and bank are
    // checked against the order rather than assumed to be the ones the resolution ran for.
    const provider = order.deliveryMethodInternal === DeliveryMethodType.BankDeposit
      && this.bankDepositSmartRoutingProvider.isEnabledForCountry(order.countryTo)
      && this.bankDepositSmartRoutingProvider.isSmartRoutedTransaction
        ? this.bankDepositSmartRoutingProvider.bankDepositProvider
        : null;

    if (provider && order.recipient && Number(order.recipient.bankId) !== Number(provider.bankId)) {
      LoggerProvider.error(this, LoggerEvent.ProviderException,
        `Smart routing resolved provider for bank ${provider.bankId} but the recipient is saved against bank ${order.recipient.bankId}, staying on legacy routing`);
      this.applyBankDepositRouting(order, null);
      return;
    }

    this.applyBankDepositRouting(order, provider);
  }

  /** The id assignment on its own, so send again can apply a provider it resolved headlessly. */
  applyBankDepositRouting(order: IOrder, provider: IBankDepositSmartRoutingProvider | null) {
    if (!provider) {
      // Cleared for every method this method is reached with, not just BankDeposit: nothing else in the
      // flow drops a resolved provider, so switching to a debit card payout would otherwise keep routing
      // ids from the bank deposit that preceded it.
      order.isSmartRouting = false;
      order.bankDepositProvider = null;
      // agentToLocation already carries the saved payout agent, and agentTo is null for a legacy bank
      // deposit. A correspondent left behind here outranks it in getCorrespId and on agentToId.
      if (order.deliveryMethodInternal === DeliveryMethodType.BankDeposit)
        order.agentTo = null;
      return;
    }

    order.isSmartRouting = true;
    order.bankDepositProvider = provider;
    // agentTo is null for a legacy bank deposit, so both halves need setting. Only the id is known
    // here — the correspondent has no name, city or payout type on this response.
    const correspondent: Partial<IAgent> = { id: provider.correspondentId };
    order.agentTo = correspondent as IAgent;
    order.agentToLocation = { agentId: provider.correspondentId, locationId: provider.correspondentLocId };
    LoggerProvider.info(this, `Bank deposit routed through smart routing provider ${provider.providerId}, correspondent ${provider.correspondentId}/${provider.correspondentLocId}`);
  }

  async selectPaymentAccount(newRecipient, replacePage: boolean = false, onContinue = null) {
    let initiatePlaid = false;
    const order = this.orderProvider.getCurrentOrder();
    
    this.logDeliveryMethodSelectedEvent(order);

    let targetPage;
    let targetParams = {};

    if (order.cardAccount)
      order.cardAccount.cvn = null;

    if (!this.orderProvider.requirePaymentSelection(order)) {
      const order = this.orderProvider.getCurrentOrder();
        const paymentObj = this.orderProvider.getAnalyticsPaymentMethod(order.paymentMethod);
        this.analytics.trackEvent({ eventName: AnalyticsEventType.PaymentMethodSelected, analyticsData: {
          ...paymentObj,
          location: SegmentLocationNames.SendMoney.toString(),
          senderCountry: APP_CONFIG.region.defaultCountryFrom,
        }});
      targetPage = this.routeProvider.pages.SendSummaryPage;
    } else if (order.paymentMethod === PaymentMethodType.StagedOnline) {
      const order = this.orderProvider.getCurrentOrder();
      const paymentObj = this.orderProvider.getAnalyticsPaymentMethod(order.paymentMethod);
      this.analytics.trackEvent({ eventName: AnalyticsEventType.PaymentMethodSelected, analyticsData: {
        ...paymentObj,
          location: SegmentLocationNames.SendMoney.toString(),
          senderCountry: APP_CONFIG.region.defaultCountryFrom,
      }});
      targetPage = this.routeProvider.pages.SendSummaryPage;
    } else if (order.paymentMethod === PaymentMethodType.PayNearMe || order.paymentMethod === PaymentMethodType.DirectBank || order.paymentMethod === PaymentMethodType.Giropay) {
      const order = this.orderProvider.getCurrentOrder();
      const paymentObj = this.orderProvider.getAnalyticsPaymentMethod(order.paymentMethod);
      this.analytics.trackEvent({ eventName: AnalyticsEventType.PaymentMethodSelected, analyticsData: {
        ...paymentObj,
          location: SegmentLocationNames.SendMoney.toString(),
          senderCountry: APP_CONFIG.region.defaultCountryFrom,
      }});
      targetPage = this.routeProvider.pages.SendSummaryPage;
    } else if (order.paymentMethod === PaymentMethodType.ApplePay || order.paymentMethod === PaymentMethodType.GooglePay) {
      targetPage = this.routeProvider.pages.SendSummaryPage;
    } else if (order.paymentMethod === PaymentMethodType.ApplePayCredit || order.paymentMethod === PaymentMethodType.GooglePayCredit) {
      targetPage = this.routeProvider.pages.SendSummaryPage;
    } else if (order.paymentMethod === PaymentMethodType.OpenBanking) {
      const order = this.orderProvider.getCurrentOrder();
      const paymentObj = this.orderProvider.getAnalyticsPaymentMethod(order.paymentMethod);
      this.analytics.trackEvent({ eventName: AnalyticsEventType.PaymentMethodSelected, analyticsData: {
        ...paymentObj,
          location: SegmentLocationNames.SendMoney.toString(),
          senderCountry: APP_CONFIG.region.defaultCountryFrom,
      }});
      targetPage = this.routeProvider.pages.SendSummaryPage;
    } else {
      this.loading.showDelayed();
      const paymentAccounts = await this.paymentProvider.getPaymentAccounts();
      let visiblePaymentAccounts;
      if (order.paymentMethod === PaymentMethodType.BankAccount)
        visiblePaymentAccounts = paymentAccounts.bankAccounts;
      else
        visiblePaymentAccounts = paymentAccounts.cards.filter(card => card.isDebit === (order.paymentMethod === 'DebitCard'));

      if (visiblePaymentAccounts.length === 0) {
        if (APP_CONFIG.region.isPlaidEnabled && order.paymentMethod === PaymentMethodType.BankAccount) {
          targetParams = {
            paymentMethod: order.paymentMethod,
            onCreated: (account) => { this.selectedPaymentAccount(account, true); }
          };
          initiatePlaid = true;
        } else {
          targetParams = {
            cardType: order.paymentMethod,
            paymentMethod: order.paymentMethod,
            onCreated: (account) => { this.selectedPaymentAccount(account, true); }
          };
          targetPage = this.routeProvider.pages.AddCardPage;
        }
      }
      else {
        targetPage = this.routeProvider.pages.SendWithPaymentsPage
        targetParams = this.getSendWithPaymentsParams(paymentAccounts, onContinue);
      }
    }

    if (targetPage === this.routeProvider.pages.SendSummaryPage && onContinue)
      onContinue();
    else if (initiatePlaid) {
      // IMPORTANT initiatePlaid doesn't have a targetPage hence ensure to check it first
      this.plaidProvider.plaidNative(targetParams);
    }
    else if (newRecipient) {
      if (!this.routeProvider.hasPage(this.routeProvider.pages.SendToRecipientsPage)) {
        targetParams = { ...targetParams, onBack: () => {
          this.routeProvider.replace(this.routeProvider.pages.SendToRecipientsPage, this.getSendToRecipientsParams(), null, false, true);
        }};
        this.routeProvider.replace(targetPage, targetParams, this.lastRecipientPage);
      }
      else
        // this.routeProvider.replace(targetPage, targetParams, this.lastRecipientPage, true);
        this.routeProvider.push(targetPage, targetParams);
    }
    else if (replacePage)
      this.routeProvider.replace(targetPage, targetParams);
    else
      this.routeProvider.push(targetPage, targetParams);
    this.lastPaymentPage = this.routeProvider.getLastPushedPage();
    this.loading.hide();
  }

  getSendWithPaymentsParams(paymentAccounts = null, onContinue = null) {
    const order = this.orderProvider.getCurrentOrder();
    return {
      paymentAccounts: paymentAccounts,
      paymentMethod: order.paymentMethod,
      cardType: order.paymentMethod,
      onAdd: () => { this.addAccount(); },
      onSelect: (account) => { this.selectedPaymentAccount(account, false, onContinue); },
      onCreated: (account) => { this.selectedPaymentAccount(account, true, onContinue); }
    };
  }

  async selectBankDeposit(recipient, newRecipient: boolean = false, onContinue = null) {
    const order = this.orderProvider.getCurrentOrder();
    order.recipient = recipient;

    this.finalizeRecipient(newRecipient, onContinue);
  }

  preSelectedLocation(location) {
    const order = this.orderProvider.getCurrentOrder();
    let payOutMethod = '';
    if(order.deliveryMethodInternal === DeliveryMethodType.OfficePickup)
      payOutMethod = AnalyticsPayoutMethodType.OfficePickup;
    if(order.deliveryMethodInternal === DeliveryMethodType.HomeDelivery)
      payOutMethod = AnalyticsPayoutMethodType.HomeDelivery;
    if (!order.cashPickUpDetails)
      order.cashPickUpDetails = {};
    order.cashPickUpDetails.exchangeRate = location.customerRate;
    order.cashPickUpDetails.payAgentId = location.id;
    order.cashPickUpDetails.payAgentName = location.name;
    let eventname = AnalyticsEventType.PayoutPartnerSelected;
    this.analytics.trackEvent({ eventName: eventname, analyticsData: {
      payoutMethod: payOutMethod,
      payoutAgentId: SegmentProvider.formatInt(location.id),
      payoutAgentName: location.name,
      location: SegmentLocationNames.SendMoney,
      payoutAgentType: (location.isOpenPayment) ? PayoutAgentType.Open : PayoutAgentType.Specific,
      recipientBankId: (order.recipient) ? order.recipient.bankId : '',
      recipientBankName: (order.recipient) ? order.recipient.bankName : '',
    }});
  }

  selectedLocation(location, agentLocation, onContinue = null) {
    const order = this.orderProvider.getCurrentOrder();
    order.agentTo = location;
    order.agentToLocation = agentLocation;
    this.finalizeRecipient(false, onContinue);
  }

  selectedPaymentAccount(account, newAccount = false, onContinue = null) {
    const order = this.orderProvider.getCurrentOrder();
    const paymentObj = this.orderProvider.getAnalyticsPaymentMethod(order.paymentMethod);
    this.analytics.trackEvent({ eventName: AnalyticsEventType.PaymentMethodSelected, analyticsData: {
      ...paymentObj,
      location: SegmentLocationNames.SendMoney.toString(),
      senderCountry: APP_CONFIG.region.defaultCountryFrom,
      
    }});

    order.bankAccount = null;
    order.cardAccount = null;

    if (account.bank)
      order.bankAccount = account.bank;
    else if (account.card) {
      order.cardAccount = account.card;
      if (typeof(order.cardAccount.isCvnRequired) === 'undefined') {
        if (order.cardAccount.type === 'Maestro')
          order.cardAccount.isCvnRequired = false;
        else
          order.cardAccount.isCvnRequired = true;
      }
      if (order.cardAccount.isDebit && order.paymentMethod === PaymentMethodType.CreditCard)
        order.paymentMethod = PaymentMethodType.DebitCard;
      else if (!order.cardAccount.isDebit && order.paymentMethod === PaymentMethodType.DebitCard)
        order.paymentMethod = PaymentMethodType.CreditCard;
    }

    if (onContinue)
      onContinue();
    else if (newAccount) {
      if (!this.routeProvider.hasPage(this.routeProvider.pages.SendWithPaymentsPage)) {
        const params = {
          onBack: () => {
          let paymentParams: any = this.getSendWithPaymentsParams(null, onContinue);
          if (!this.routeProvider.hasPage(this.routeProvider.pages.SendToRecipientsPage)) {
            // if also a new recipient has been created we don't have the recipient list and have to add it now
            paymentParams = { ...paymentParams, onBack: () => {
              this.routeProvider.replace(this.routeProvider.pages.SendToRecipientsPage, this.getSendToRecipientsParams(), null, false, true);
            }};
          }
          this.routeProvider.replace(this.routeProvider.pages.SendWithPaymentsPage, paymentParams, null, false, true);
        }};
        const hasCalculatorPage = this.routeProvider.hasPage(this.routeProvider.pages.CheckPricesPage);
        if (!hasCalculatorPage && this.routeProvider.getCurrentTabName() === this.routeProvider.pages.ActivityPage.page) {
          this.routeProvider.openModal(this.routeProvider.pages.SendSummaryPage, params);
        } else {
          this.routeProvider.replace(this.routeProvider.pages.SendSummaryPage, params, this.lastPaymentPage);
        }
      }
      else if (account && account.bank && account.bank.verificationStatus === BankAccountVerificationStatus.WaitingOnVerificationDeposits) {
        this.paymentProvider.getPaymentAccounts(true); // reload payment accounts to include new one
        this.routeProvider.popToOrPush(this.routeProvider.pages.PaymentAccountListPage, {paymentMethod: PaymentMethodType.BankAccount});
    }
      else
        this.routeProvider.replace(this.routeProvider.pages.SendSummaryPage, { }, this.lastPaymentPage, true);
    }
    else {
      if (account.card) {
        if (account.card.isExpired)
          this.routeProvider.push(this.routeProvider.pages.PaymentAccountDetailsPage, { onContinue: () => {
            this.routeProvider.replace(this.routeProvider.pages.SendSummaryPage, { });
            },
            card: account.card
          });
        else if(account.card.isCvnRequired){
          this.routeProvider.push(this.routeProvider.pages.SendCvvPage, { onContinue: () => {
            let analyticsPayObj = this.orderProvider.getAnalyticsPaymentMethod(order.paymentMethod);
            this.analytics.trackEvent({ eventName: AnalyticsEventType.CvvEntered, analyticsData: {
              location: SegmentLocationNames.SendMoney.toString(),
              senderCountry: APP_CONFIG.region.defaultCountryFrom,
              ...analyticsPayObj,
            } });
            this.routeProvider.replace(this.routeProvider.pages.SendSummaryPage, { });
            },
            card: account.card
          });
        }else{
          this.routeProvider.replace(this.routeProvider.pages.SendSummaryPage, { });
        }
      }
      else {
        // replace up to payment selection page because eventually bank verification has been done in-between and should be removed from the navigation stack
        this.routeProvider.replace(this.routeProvider.pages.SendSummaryPage, { }, this.routeProvider.pages.SendWithPaymentsPage, true);
      }
    }
  }

  async confirmOrder(sendOrderRegardlessOnHold: boolean, recipient, fromRequired: boolean = false, proceed: any = null, callback: () => void = null,onfidoProvider: OnfidoProvider, isValidate?:boolean, fetchRequiredFields?:boolean){
    // IMPORTANT:
    // when we start to create an order remove the pending order since if we run into a problem we might not know if the order has been created
    // on the backend (i.e. timeout during order creation) hence if the user logs in again or goes back to activity he could see the continue order CTA
    // which could lead him to create an order twice (remember Juan) !
    // Hence remove the pending order to be on the safe side
    if (sendOrderRegardlessOnHold)
      await this.orderProvider.removePendingOrder();

    this.recipient = recipient;
    const order = this.orderProvider.getCurrentOrder();

    if (!isCurrencyAllowedForCustomer(order && order.currencyTo)) {
      LoggerProvider.warning(this, LoggerEvent.SendMoneyPage,
        `Blocked confirmOrder for disallowed stablecoin payout currency: ${order && order.currencyTo}`);
      this.loading.hide();
      showStablecoinUnavailableModal(this.routeProvider, this.orderProvider, this.translate);
      return;
    }

    if(order.walletAdditionFields) {
      if(!order.stagedProvidedFields){
        order.stagedProvidedFields = order.walletAdditionFields;
      }else{
        let fieldsWallet = {};
        let fieldsProvided = {};
        let allfields = {};
        order.walletAdditionFields.forEach(field=>{ fieldsWallet[field.FieldId] = field.Value} );
        order.stagedProvidedFields.forEach(field=>{ fieldsProvided[field.FieldId] = field.Value });
        allfields = {...fieldsWallet, ...fieldsProvided};

        let newArray = [];
        Object.keys(allfields).forEach(key=>{let keyAsNumber = parseInt(key); newArray.push({'FieldId':keyAsNumber ? keyAsNumber : key,'Value':allfields[key]})});
        order.stagedProvidedFields = newArray;
      }
    }

    // Removing wallet PIN if in their in request
    if (fetchRequiredFields && Array.isArray(order.stagedProvidedFields) && order.stagedProvidedFields.length > 0) {
      const filteredFields = order.stagedProvidedFields.filter(obj => obj.FieldId !== 19201);
      order.stagedProvidedFields = filteredFields
    };

    if(!fromRequired) this.loading.show();
    var isSignicatEnabled;
    
    try {
      isSignicatEnabled = await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry();
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get country features');
    }

    if (OrderProvider.AccertifyTransactionID) 
      order.accertifyDeviceId = OrderProvider.AccertifyTransactionID;
    else {
      await this.orderProvider.initializeAccertify();
      order.accertifyDeviceId = OrderProvider.AccertifyTransactionID;
    }
    
    if (!order.accertifyDeviceId)
      LoggerProvider.warning(this, LoggerEvent.AccertifyException, 'Accertify: Failed to get device ID!');

    this.customersProvider.orderConfirm(
        order,
        sendOrderRegardlessOnHold,
        true,//this.veracityDisclosure,
        isValidate,
        true,//this.acceptsTermsAndConditions
        fetchRequiredFields
      ).subscribe(
      res => {
        switch (res.Result.RetVal) {
          case RET_VAL.HAPPY_PATH:
            /*this.utils.registerEvent(EVENTS_ID.USER_CREATES_STATEG_ORDER).subscribe();
            */
            this.customersProvider.getTransaction(res.Value.OrderId).subscribe(async x => {
              if (x) {
                const transactionInfo = await this.customersProvider.getTransactionInfo(true);
                // IMPORTANT: don't add +1 here because it's different then the other countEnteredOrders calculation in send-finish
                // Here the order has been created already so totalTransactionCount will be 1 while in the other cases no order has been created yet hence the value would be 0 and would need a +1
                order.countEnteredOrders = this.orderProvider.getAdjustedTotalTransactionCount(transactionInfo);
                if(!order.orderNumber || order.orderNumber === "" || order.resend) {
                  order.orderNumber = (res.Value && res.Value.OrderNo) ? res.Value.OrderNo : 0;
                }
                
                this.utils.setTransactionDotState(true);
                this.orderProvider.resetStoreOrder();
                this.sendTransactionCreatedAnalyticsEvent(order);

                this.navigateToFinalPage(order.paymentMethod, order, x);
              }
            });
            this.loading.hide();
            break;
          /*case RET_VAL.WRONG_SENT_AMOUNTS:
            this.loading.hide();
            this.wrongSentAmounts();
            break;*/
          case RET_VAL.REQUIRED_IDENTITY_VERIFICATION:
            let params = {
              onSuccess: () => {
                this.routeProvider.replace(this.routeProvider.pages.SendFinishPage);
              },
              analyticsLocation: SegmentLocationNames.SendMoney,
              missing: res.Result.RetMsg,
              goBackToSendSummary: true,
              kycProcessor: res.Value && res.Value.KycProcessor ? res.Value.KycProcessor : null
            };
            this.loading.hide();
            if (res.Result.RetMsg && res.Result.RetMsg !== MissingTransactionInfoType.RequiresIdentityVerification)
              this.pushConfirmOrderPage(this.routeProvider.pages.SendFailedPage, params);
            else {
              if (isSignicatEnabled) {
                this.pushConfirmOrderPage(this.routeProvider.pages.EkycMethodSelectPage, params);
              } else {
                onfidoProvider.setOriginPage(this.routeProvider.pages.CheckPricesPage);
                this.pushConfirmOrderPage(this.routeProvider.pages.OnfidoInfoPage, params);
              }
            }
            break;
          case RET_VAL.HIGHER_LIMIT_IDENTITY_VERIFICATION:
            params = {
              onSuccess: () => {
                this.routeProvider.replace(this.routeProvider.pages.SendFinishPage);
              },
              analyticsLocation: SegmentLocationNames.SendMoney,
              missing: res.Result.RetMsg,
              goBackToSendSummary: true,
              kycProcessor: res.Value && res.Value.KycProcessor ? res.Value.KycProcessor : null,
            };

            this.loading.hide();
            if (res.Result.RetMsg && res.Result.RetMsg !== MissingTransactionInfoType.HigherLimitIdentityVerificationRequired)
              this.pushConfirmOrderPage(this.routeProvider.pages.SendFailedPage, params);
            else {
                onfidoProvider.setOriginPage(this.routeProvider.pages.CheckPricesPage);
                this.pushConfirmOrderPage(this.routeProvider.pages.OnfidoInfoPage, params);
            }
            break;
          case RET_VAL.REQUIRED_PIN_CODE:
            let invalidPinAttempts = 0;
            if (res.Value) {
              let mapper = FormControlMapper.mapRequiredFields(res.Value.RequiredFields);
              this.customerFields = mapper[0];
              this.recipientFields = mapper[1];
              invalidPinAttempts = res.Value.RiaeWalletInvalidPinAttempts;
              if(invalidPinAttempts >0){
                this.analytics.trackEvent({eventName:AnalyticsEventType.SecurityMethodFailed,analyticsData:{securityMethod:'Passcode',location:'SMF'}});
                this.analytics.trackEvent({eventName:AnalyticsEventType.PasscodeChallengeFailed,analyticsData:{country:APP_CONFIG.region.defaultCountryFrom, failureReason:(res && res.Result && res.Result.RetMsg) ? res.Result.RetMsg: "", errorCode:UtilsProvider.getResultRetVal(res), location: PasscodeChallengeLocation.SendMoney}});
              }
            }
            this.sendFinishPage(this.recipientFields, recipient, proceed, true, invalidPinAttempts);
            break;
          case RET_VAL.PIN_CODE_WALLET_MAX_ATTEMPTS:
          case RET_VAL.SN_PIN_MAX_ATTEMPTS:
            const data = {
              lockedReason: 'Invalid pin',
              location: 'send-money'
            };
            this.analytics.trackEvent({eventName:AnalyticsEventType.SecurityMethodFailed,analyticsData:{securityMethod:'Passcode',location:'SMF'}});
            this.analytics.trackEvent({eventName:AnalyticsEventType.PasscodeChallengeFailed,analyticsData:{country:APP_CONFIG.region.defaultCountryFrom, failureReason:(res && res.Result && res.Result.RetMsg) ? res.Result.RetMsg: "", errorCode:UtilsProvider.getResultRetVal(res), location: PasscodeChallengeLocation.SendMoney}});
            this.pushConfirmOrderPage(this.routeProvider.pages.WalletBlockedPage, data);
            break;
          case RET_VAL.WATCHLIST_MATCHED:
          case RET_VAL.SAR_VALIDATION_ON_HOLD:
          case RET_VAL.REQUIRED_FIELDS_MATCHED:
            if (res.Value) {
              let mapper = FormControlMapper.mapRequiredFields(res.Value.RequiredFields);
              this.customerFields = mapper[0];
              this.recipientFields = mapper[1];
            } else {
              this.customerFields = [];
              this.recipientFields = [];
            }
            if ((this.customerFields && this.customerFields.length > 0) || (this.recipientFields && this.recipientFields.length > 0))
              this.sendFinishPage(this.recipientFields, recipient, proceed);
            else {
              LoggerProvider.error(this, LoggerEvent.ProviderException, 'Should show additional details but no additional fields provided !');
              this.pushConfirmOrderPage(this.routeProvider.pages.SendFailedPage, { errorDetails: this.translate.instant('global.myRia.jsonContent.NewTransactionError'), stagedError: true });
            }
            break;
          case RET_VAL.WALLET_TXN_COUNT_LIMIT_REACHED:
          case RET_VAL.WALLET_TXN_AMOUNT_LIMIT_REACHED:
          case RET_VAL.WALLET_TXN_LIMIT_EXCEEDED:
            const param = {
              primaryButtonAction: () => {this.routeProvider.setRoot(this.routeProvider.pages.TabsHomePage); }
            };
            this.pushConfirmOrderPage(this.routeProvider.pages.EWalletReachedDailyLimitErrorPage, param);
            this.loading.hide();
            break;
          /*case RET_VAL.NO_CONTACT_DATA:
            this.routeProvider.setRoot({page:PAGES.RESULT_INFO}, { page: RESULT_INFO_PAGES.NO_CONTACT_DATA });
            this.loading.hide();
            break;
          case RET_VAL.VERIFY_PHONE_NUMBER:
              this.routeProvider.setRoot({page:PAGES.LINK_CUSTOMER_SMS}, {resendAction: RESEND_ACTIONS.RESET_PASSWORD, customerPhone: res.Value.MaskedPhone});
              this.loading.hide();
              break;*/
          case RET_VAL.FRAUD_VERIFICATION_FAILED_CUSTOMER:
              this.loading.hide();
              // return RET_VAL.FRAUD_VERIFICATION_FAILED_CUSTOMER;
              callback();
            break;
          case RET_VAL.MISSING_BANK_DETAILS:
            this.showSelectBankDeposit(order, true).then(() => { this.loading.hide(); }, () => { this.loading.hide(); });
            break;
          case RET_VAL.MISSING_PAYMENT_METHOD:
            this.orderProvider.showDeliveryMethodError(order);
            this.loading.hide();
            break;
          case RET_VAL.FRAUD_CHECK_FAILURE_EXCEPTION:
            const onClose = () => { this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage); };
            const error = { errorCode: ErrorCodeType.FraudCheckFailureException };
            this.routeProvider.replace(this.routeProvider.pages.SendFailedPage, { error: error, digitalError: true, clickClose: onClose });
            break;
          default:
            this.pushConfirmOrderPage(this.routeProvider.pages.SendFailedPage, { errorDetails: this.translate.instant('global.myRia.jsonContent.NewTransactionError'), stagedError: true });
            this.loading.hide();
            break;
        }
      }, err => {
        LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'Order confirm failed');
        if(err.status == 307) {
          this.platform.ready().then(() =>{
            let securityCode = this.platform.is('cordova') ? err.headers['securitycode'] : err.headers.get('securityCode');
            let referenceValue = this.platform.is('cordova') ? err.headers['referencevalue'] : err.headers.get('referenceValue');
            let maskedPhone= this.platform.is('cordova') ? err.headers['maskedphone'] : err.headers.get('maskedPhone');

            let modal = this.modalCtrl.create(PAGES.OTP_SCREEN, {
              otpPageType: OTP_ACTIONS.NEW_ORDER,
              securityCode: securityCode,
              referenceValue: referenceValue,
              maskedPhone: maskedPhone
            });
            modal.present();
            modal.onDidDismiss (res => {
              if(!res.blocked) {
                order.otpActionRequest = { securityCode: res.securityCode ,referenceValue: err.headers.get('ReferenceValue')}
                this.confirmOrder(sendOrderRegardlessOnHold, recipient, null, null, null, onfidoProvider);
              }
            });
          });
        }
        else if(err.status == 401) {
          this.loading.hide();
          RmtPopupComponent.showExceptionWithLogTranslation(this, err, "page.errorMessage.ria_errormessage_customeronhold");
          this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
        }else {
          this.loading.hide();
          RmtPopupComponent.showExceptionWithLogTranslation(this, err, 'global.myRia.jsonContent.OopsInfo')
          this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
        }
      });
  }

  private async hydrateNationalityOptionsIfMissing(customerFields: any[]): Promise<void> {
    const nationalityField = customerFields.find(f => {
      const fieldName = f.fieldModel && f.fieldModel.FieldName ? String(f.fieldModel.FieldName).toLowerCase() : '';
      return f.id === '16601' || fieldName === 'custnationality';
    });

    if (!nationalityField || (Array.isArray(nationalityField.options) && nationalityField.options.length > 0)) {
      return;
    }

    const countries = this.resourceProvider.countries;
    if (!countries || countries.length === 0) {
      return;
    }

    nationalityField.options = countries.map(c => ({ value: c.value, text: c.text, displayPriority: null, category: null }));
    if (nationalityField.fieldModel) {
      nationalityField.fieldModel.Options = countries.map(c => ({ Id: c.value, Name: c.text }));
    }
    nationalityField.hasAsyncValues = false;
  }

  private sendToUploadDocumentRequestPage(order: IOrder, objValue: any, objTransaction: any) {
    order.pin = objValue.PIN;
    order.orderNumber = objValue.OrderNo;
    order.uploadReason = objValue.UploadReason;
    order.uploadRequestNo = objValue.UploadRequestNo;

    const transact = new Transaction();
    transact.OrderNo = order.orderNumber;
    transact.UploadReason = order.uploadReason;
    transact.UploadRequestNo = order.uploadRequestNo;

    this.routeProvider.replace(this.routeProvider.pages.sendToUploadDocumentRequestPage, {
      buttonLeftClick: (routeProvider) => {
        routeProvider.replace(routeProvider.pages.DocUploadSelectPage, { 'transaction': transact, 'fromFinish': true, 'continueFinishOrder': true });
      },
      secondaryAction: (routeProvider) => {
        this.navigateToFinalPage(order.paymentMethod, order, objTransaction);
      },
      removeHeader: true,
    });
  }

  sendTransactionCreatedAnalyticsEvent(order: IOrder) {
    let analyticsDataSN ={};
    if(APP_CONFIG.region.region == Region.SN && order){
      if(order.paymentMethod === PaymentMethodType.RiaWallet){
        this.analytics.trackEvent({eventName:AnalyticsEventType.PasscodeChallengeSuccessful ,analyticsData:{country: APP_CONFIG.region.defaultCountryFrom, location:PasscodeChallengeLocation.SendMoney}});
      }
      analyticsDataSN ={feeUSD: order.customerFeeUSD, newCustomerReferral: order.IsReferralPromo ? order.IsReferralPromo : false, location: "Send Money", transferFlowType: order.resend ? "Resend" : "From Scratch"}
    }
    const isFirstOrder = (order.countEnteredOrders && order.countEnteredOrders === 1)

    this.analytics.trackEvent({eventName: AnalyticsEventType.TransactionCreated, analyticsData: {
      promoRate: order.exchangeRatePromo ? order.exchangeRatePromo : null,
      earlyReleased: false,
      transferFlowType: order.resend ? "Resend": "From Scratch",
      ...this.orderProvider.getAnalyticsDataFromOrder(order, true, isFirstOrder),
      ...analyticsDataSN,
      ...getSourceAnalyticsData(),
      ...getCampaignAnalyticsData()
    } });
  }

  navigateToFinalPage(paymentMethod, order, transaction) {
    order.orderSent = true;
    this.orderProvider.setCurrentOrder(order); // ensure that it's our order not an order in MyRIA format (OrderConfirm)

    if (APP_CONFIG.region.region === Region.CL) {
      if (paymentMethod === PaymentMethodType.SencillitoOnline) {
        this.routeProvider.setRoot(this.routeProvider.pages.SendPaymentPendingSencillito, { transaction, paymentMethod: paymentMethod });
      }
      else {
        this.routeProvider.setRoot(this.routeProvider.pages.SendPaymentPendingPayInStore, { transaction, paymentMethod: paymentMethod });
      }
    }
    else if (paymentMethod === PaymentMethodType.PayID) this.routeProvider.setRoot(this.routeProvider.pages.SendPaymentPendingPayID, { transaction, paymentMethod: paymentMethod });
    else if (paymentMethod === PaymentMethodType.StagedOnline) this.orderProvider.setPaynowRoute(transaction);
    else // Only for PaymentMethod.StagedRia
      this.routeProvider.push(this.routeProvider.pages.SendFinishPage, {stagedOrder: transaction, finished: true});
    if (!this.orderProvider.shouldDeferOrderCreatedEvents(order)) {
      this.branchProvider.trackOrderEvents(order, this.customersProvider);
      this.firebaseAnalyticsProvider.trackFirstOrder(order);
    }
  }

  async sendFinishPage(otherFieldRemoved, recipient, proceed, isPinRequired = false, InvalidPinAttempts = -1) {
    const order = this.orderProvider.getCurrentOrder();
    const pageParams = {
      onContinue: () => {
        const analyticsPayObj = this.orderProvider.getAnalyticsPaymentMethod(order.paymentMethod);
        this.analytics.trackEvent({ eventName: AnalyticsEventType.CvvEntered, analyticsData: {
          location: SegmentLocationNames.SendMoney.toString(), // TODO
          senderCountry: APP_CONFIG.region.defaultCountryFrom,
          ...analyticsPayObj
        } });
        this.routeProvider.replace(this.routeProvider.pages.SendFinishPage);
      }
    };

    if (this.customerFields && this.customerFields.length > 0)
      await this.hydrateNationalityOptionsIfMissing(this.customerFields);

    let additionalSteps = null;
    if ((this.customerFields && this.customerFields.length > 0) || (otherFieldRemoved && otherFieldRemoved.length > 0))
      additionalSteps = await this.additionalDetailsProvider.getAdditionalFieldSteps(this.customerFields, otherFieldRemoved);

    if (additionalSteps && additionalSteps.length > 0) {
      if (!isPinRequired) { // most common scenario
        const payload = {
          recipient: recipient,
          steps: additionalSteps,
          onContinue: (params) => {
            this.continueOrder(pageParams, proceed, params);
          }
        };
        order.stagedProvidedFields = [];
        this.orderProvider.setCurrentOrder(order);
        this.routeProvider.push(this.routeProvider.pages.SendAdditionalFieldsPage, payload);
      } else {
        this.loading.hide();
        const payload = {
          recipient: recipient,
          customerFields: this.customerFields,
          recipientFields: otherFieldRemoved,
          actionType: PinActionType.Transfer,
          showBack: true,
          gotoSummary:true,
          WrongPinAttemptsCount : InvalidPinAttempts > 0 ? InvalidPinAttempts : 0,
          onContinue: (params) => {
            this.continueOrder(pageParams, proceed, params);
          }
        };
        this.routeProvider.push(this.routeProvider.pages.WalletPinPage, payload);
      }
    } else {
      const requirePayment = this.continueOrderPayment(order, pageParams);
      if (requirePayment)
        LoggerProvider.info(this, 'Require payment in send finish page');
      else if (proceed && proceed.onContinue) proceed.onContinue(false, {});
      else this.routeProvider.replace(this.routeProvider.pages.SendFinishPage);
    }
  }

  continueOrderPayment(order, pageParams, replacePage = false) {
    if (order.cardAccount && order.cardAccount.isCvnRequired && !order.cardAccount.cvn && !order.pspPaymentMethod) {
      this.routeProvider.pushOrReplace(replacePage, this.routeProvider.pages.SendCvvPage, pageParams);
      return true;
    }
    return false;
  }

  continueOrder(pageParams, proceed, params) {
    const order = this.orderProvider.getCurrentOrder();
    const requirePayment = this.continueOrderPayment(order, pageParams, true);
    if (requirePayment)
      LoggerProvider.info(this, 'Require payment in continue order');
    else if (proceed && proceed.onContinue) proceed.onContinue(true, params);
    else this.routeProvider.replace(this.routeProvider.pages.SendFinishPage);
  }

  confirmMissingFieldsTransaction(recipient, onfidoProvider: OnfidoProvider) {
    this.alert.showAlert(this.translate.instant('global.myRia.jsonContent.RetValOnHold'), '', [
      {
        text: this.translate.instant('global.genericCancelButton'),
        role: 'cancel',
        handler: () => this.routeProvider.push(this.routeProvider.pages.SendFailedPage, { errorDetails: this.translate.instant('global.myRia.jsonContent.NewTransactionError'), stagedError: true })
      },
      {
        text: this.translate.instant('global.confirmButton'),
        handler: () => this.confirmOrder(SEND_REGARDLESS_ONHOLD, recipient, null, null, null, onfidoProvider)
      }
    ], true, '');
  }

  wrongSentAmounts() {
    /*if (this.loginProvider.isCustomerOnHold()) {
      this.routeProvider.setRoot({page:PAGES.RESULT_INFO}, { retVal: RET_VAL.CUSTOMER_ON_HOLD, page: RESULT_INFO_PAGES.NOT_DEFINED });
    } else {
      this.loading.show();
      let transaction = new NewTransactionPreview(
        //this.newOrder.Transaction,
        //this.newOrder.EntryOrder,
        //this.newOrder.DestinationDeliveryMethod,
        //this.newOrder.DestinationCountry,
        //this.newOrder.DestinationLocation ? //this.newOrder.DestinationLocation.CorrespId : this.newOrder.RequiredBankInfo.Provider.CorrespId,
        //this.newOrder.DestinationLocation ? //this.newOrder.DestinationLocation.Id : this.newOrder.RequiredBankInfo.Provider.CorrespLocId
      );
      this.customersProvider.getNewTransactionPreview(transaction).subscribe(
        x => {
          this.events.publish(EVENTS_NAME.SHOW_ALERT, {
            title: this.translate.instant('RetValOrderRestrictionsMatchedTitle'),
            msg: this.translate.instant('RetValOrderRestrictionsMatchedSubtitle'),
            buttons: ['OK'],
            enableDismiss: true
          });
          this.veracityDisclosure = true;
          this.acceptsTermsAndConditions = true;
          x = this.convertAmountsModified(x);
          //this.markModifiedField(this.newOrder.Transaction, x);
          this.loading.hide();
        },
        () => this.loading.hide()
      );
    }*/
  }

  async sendMoneyBtn(sendFromActivity?: boolean, sendFromNewWidgetRate?: boolean, countryTo?) {
    this.loading.showDelayed();
    const isLiteUser = this.loginProvider.isLiteUser();
    let showCountries = false;
    const cachedPreConfirmOrder = await this.orderProvider.getCachePreConfirmOrder();
    if (!cachedPreConfirmOrder) {
      showCountries = true;
      if (!isLiteUser) {
        let promiseResolve = null;
        let timeout = null;

        // get the orders but we have to ensure that if there is a problem we can still continue with the send money flow
        // hence if getting orders failed, we continue and we have a timeout of a few seconds to get the result otherwise we will continue without it
        const promise = new Promise(async(resolve) => {
          promiseResolve = resolve;
          try {
            const orders = await this.customersProvider.getTransactionsGroupedByStatus(0, null, false, TransactionViewType.Any);
            if (orders && orders.length > 0)
              showCountries = false;
          } catch (ex) {
            LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to get orders for send money');
          }
          promiseResolve(true);
          window.clearTimeout(timeout);
        });
        timeout = window.setTimeout(() => {
          LoggerProvider.error(this, LoggerEvent.ProviderException, 'Got timeout when waiting for orders for send money');
          promiseResolve(true);
        }, 5000);
        await promise;
      }
    }

    let defaultCountry = null;
    var customer: ICustomer;
    customer = await this.customerProvider.getCustomer();
    let countryOfBirth = (customer && customer.countryOfBirth)?customer.countryOfBirth:"";
    let userSendingCountry = await this.storageProvider.get(StorageType.userSendingCountry);
    defaultCountry = await this.storageProvider.get(StorageType.DefaultCountryTo);
    this.loading.hide();
    // if is NewWidgetRate and country to is set we don't show the country picker
    if (showCountries && this.allowCountryPicker(countryOfBirth) && (!sendFromNewWidgetRate && !countryTo && !userSendingCountry && !defaultCountry)) {
      await this.showCountryPicker(false, sendFromActivity);
      return;
    }

    this.orderProvider.setCurrentOrder(null); // start a new order
    
    if (!defaultCountry && !this.allowCountryPicker()) {
      if (customer)
        defaultCountry = customer.countryOfBirth;
    }
    defaultCountry = countryTo ? countryTo : userSendingCountry ? userSendingCountry : defaultCountry;
    this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage, {
      defaultCountry,
      sendFromActivity,
      sendFromNewWidgetRate
    });
  }

  allowCountryPicker(countryOfBirth="") { // check whether to show country picker before coming to calculator
    if (APP_CONFIG.region.region == Region.MY && countryOfBirth!== Region.MY ) // for new MY accounts, default Sending To country should be COB
      return false
    return true;
  }

  async displayCalculatorCountrySelect() {
    const params = {
      header: this.translate.instant("page.priceCalculatorPage.title"),
      title: this.translate.instant(
        "form.moneyTransferCalculatorForm.countries.label"
      ),
      placeholder: this.translate.instant("page.languagePage.countrySearch"),
      useIcons: true,
      newDesign: true,
      itemFieldIcon: "value",
      butnId: "sendMoneyFromBtnCheckPrice",
      disable: false,
      itemFieldIconPrefix: "custom-icon-country-",
      items: null,
      itemsPromise: await this.resourceProvider.getSendCountries(),
      favoritePromise: await this.resourceProvider.getTopCountries(),
    };
    this.routeProvider.openModal(
      this.routeProvider.pages.SelectPickerPage,
      params,
      (item: any) => {
        if (item) {
          const countryAbv = item.value;

          this.orderProvider.setCurrentOrder(null); // start a new order
          this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage, {
            defaultCountry: countryAbv,
          });
        }
      }
    );
  }

  async changeDelivery() {
    const order = this.orderProvider.getCurrentOrder();
    const paymentMethods = await this.resourceProvider.getPaymentMethods();
    const actionsheetNavParams = await this.calcProvider.getActionSheetNavParams(order, this.orderProvider, order.deliveryMethods, paymentMethods, order.variableRates, order.variableFees, {});
    Object.assign(actionsheetNavParams, {listType: "delivery"})
    this.routeProvider.openActionSheet(this.routeProvider.pages.CheckPricesSelectActionsheet, actionsheetNavParams , (item) => {
      if (item) {
        order.deliveryMethodInternal = item.value;
        order.deliveryMethod = item.text;
        // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
        /*
        if (this.isDestinationCountryOpenPaymentAndHasDefaultAgent()) {
          this.order.agentToLocation = { agentId: this.destinationCountry.DefaultOpenPaymentPayAgentId, locationId: this.destinationCountry.DefaultOpenPaymentPayAgentLocId };
        }
        else
          this.order.agentToLocation = null;
        */
        this.proceedDelivery();
      }
    }, 'rmt-select-actionsheet-modal');
  }

  async proceedDelivery(continueFlow = false) {
    const order = this.orderProvider.getCurrentOrder();
    let onContinue = null;
    if (!continueFlow)
      onContinue = () => {
        this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
      };
    await this.selectedRecipient(order.recipient, false, null, null, null, onContinue);
  }

  changeRecipient(continueFlow = false) {
    let onContinue = null;
    if (!continueFlow)
      onContinue = () => {
        this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
      };
    this.selectRecipient(false, onContinue);
  }

  async changePayment() {
    const order = this.orderProvider.getCurrentOrder();
    const paymentMethods = await this.resourceProvider.getPaymentMethods();
    const actionsheetNavParams = await this.calcProvider.getActionSheetNavParams(order, this.orderProvider, order.deliveryMethods, paymentMethods, order.variableRates,order.variableFees, {});
    Object.assign(actionsheetNavParams, {listType: "payment"})
    this.routeProvider.openActionSheet(this.routeProvider.pages.CheckPricesSelectActionsheet, actionsheetNavParams , (item) => {
      if (item) {
        if (order.paymentMethod !== item.value) {
          order.bankAccount = null;
          order.cardAccount = null;
          order.paymentMethod = item.value;
        }
        this.proceedPayment();
      }
    }, 'rmt-select-actionsheet-modal');
  }

  async proceedPayment(continueFlow = false) {
    let onContinue = null;
    if (!continueFlow)
      onContinue = () => {
        this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
      };
    await this.selectPaymentAccount(false, false, onContinue);
  }

  async changeAmount() {
    this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage, {
      onContinue: () => {
        this.routeProvider.pop();
      }
    });
    /*
    this.routeProvider.openModal(this.routeProvider.pages.SendChangeAmountPage, {currency: this.order.currencyFrom, amount: this.order.amountFrom}, result => {
      if(result.calculate)
        this.order.amountFrom = result.amount;
        this.calculate(true)
    });
    */
  }

  async proceedOrder() {
    this.setDifferentLocationForAnalytics();
    if (!this.orderProvider.validRecipient) {
      await this.changeRecipient(true);
    } else if (!this.orderProvider.validDelivery) {
      await this.proceedDelivery(true);
    } else if (!this.orderProvider.validPayment) {
      await this.proceedPayment(true);
    } else
      await this.routeProvider.push(this.routeProvider.pages.SendSummaryPage);
  }

  isRmt5BankDepositEnabled(): boolean {
    return !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeSendMoney);
  }

  async showSelectBankDeposit(order, replacePage: boolean, newRecipient: boolean = false, onContinue = null) {
    await this.resolveBankDepositSmartRouting(order);
    // Haiti (HT) bank deposit only reliably supports Unibank; the Vue smart-routing
    // top-banks screen lets the customer pick a bank that won't work and get stuck.
    // Force the native flow for Haiti, where the top-banks screen is skipped in
    // favour of the full bank search (see rmt-bank-deposit.openBankSearchScreen).
    const isHaitiBankDeposit = order.countryTo === 'HT';
    if (this.isRmt5BankDepositEnabled() && !isHaitiBankDeposit) {
      const entry: BridgeBankDepositEntry = {
        recipientId: order.recipient && order.recipient.id,
        countryTo: order.countryTo,
        currencyFrom: order.currencyFrom,
        currencyTo: order.currencyTo,
        amountFrom: order.amountFrom,
        amountTo: order.amountTo,
      };
      this.utils.navigateToBankDepositOrFallback(
        entry,
        (data) => this.onRMT5BankDepositComplete(data, newRecipient, onContinue),
        () => this.showNativeSelectBankDeposit(order, replacePage, newRecipient, onContinue),
      );
      return;
    }
    this.showNativeSelectBankDeposit(order, replacePage, newRecipient, onContinue);
  }

  showNativeSelectBankDeposit(order, replacePage: boolean, newRecipient: boolean = false, onContinue = null) {
    const params = {
      recipient: order.recipient,
      amount: order.amountFrom,
      currency: order.currencyTo,
      order: order,
      fromSendMoneyFlow: true,
      onSelect: (recipient) => { this.selectBankDeposit(recipient, newRecipient, onContinue); }
    };
    this.routeProvider.pushOrReplace(replacePage, this.routeProvider.pages.SendToBankDepositPage, params, null, false);
  }

  onRMT5BankDepositComplete(data: BridgeBankDepositCompleteData, newRecipient: boolean = false, onContinue = null) {
    const order = this.orderProvider.getCurrentOrder();
    const recipientId = data && data.recipientId != null
      ? Number(data.recipientId)
      : (order.recipient && order.recipient.id);
    if (recipientId == null) {
      LoggerProvider.error(this, LoggerEvent.ComponentException, 'RMT5 bank deposit completed without a recipient id');
      return;
    }

    const recipient = order.recipient && Number(order.recipient.id) === recipientId
      ? order.recipient
      : { ...(order.recipient || {}), id: recipientId };
    this.selectBankDeposit(recipient, newRecipient, onContinue);
  }

  /**
   * Send again when the routing engine changed since the original transfer: the customer re-confirms the
   * bank details on the current engine's form. Opened as a modal because send again runs over the tabs.
   */
  openBankDepositForReselection(order: IOrder, recipient, onContinue = null) {
    const params = {
      recipient,
      country: order.countryTo,
      amount: order.amountFrom,
      currency: order.currencyTo,
      order,
      fromSendMoneyFlow: true,
      preserveAccountDetails: true,
      onSelect: (selected) => { this.selectBankDeposit(selected, false, onContinue); }
    };
    this.routeProvider.openModal(this.routeProvider.pages.SendToBankDepositPage, params);
  }

  showSelectDebitCardPayout(order, replacePage: boolean) {
    const params = {
      recipient: order.recipient,
      amount: order.amountFrom,
      currency: order.currencyTo,
      order: order,
      onSelect: (recipient) => { this.selectBankDeposit(recipient); }
    };
    this.routeProvider.pushOrReplace(replacePage, this.routeProvider.pages.SendToDebitCardPayoutPage, params, null, false);
  }

  pushConfirmOrderPage(page, params) {
    let replacePage = false;
    if (this.routeProvider.hasPage(this.routeProvider.pages.SendFinishPage))
      replacePage = true;
    this.routeProvider.pushOrReplace(replacePage, page, params);
  }

  async getCountryPickerParams(onSelect, selectedValue = null) {
    const params = {
      header: this.translate.instant("page.priceCalculatorPage.title"),
      title: this.translate.instant(
        "form.moneyTransferCalculatorForm.countries.label"
      ),
      placeholder: this.translate.instant("page.languagePage.countrySearch"),
      useIcons: true,
      newDesign: true,
      itemFieldIcon: "value",
      butnId: "sendMoneyFromBtnCheckPrice",
      disable: false,
      itemFieldIconPrefix: "custom-icon-country-",
      items: null,
      itemsPromise: await this.resourceProvider.getSendCountries(),
      favoritePromise: await this.resourceProvider.getTopCountries(),
      showListHeader: true,
      ignoreAccentsDuringSearch: true,
      itemSelected: (selectedValue) ? { value: selectedValue } : null,
      onSelect,
      canShowAllItems: APP_CONFIG.region.region !== Region.SN ? true : false
    };
    return params;
  }

  async showCountryPicker(replace = false, sendFromActivity?: boolean, sendFromNewWidgetRate?: boolean ) {
    if(!this.resourceProvider.sendCountries)
      this.loading.show();
    const params = await this.getCountryPickerParams((item) => {
      const countryAbv = item.value;
      this.storageProvider.set(StorageType.DefaultCountryTo, countryAbv);

      this.orderProvider.setCurrentOrder(null); // start a new order
      this.routeProvider.replace(this.routeProvider.pages.CheckPricesPage, { defaultCountry: countryAbv, sendFromActivity, sendFromNewWidgetRate});    
    });
    this.loading.hide();
    if (replace)
      this.routeProvider.replace(this.routeProvider.pages.SelectPickerPage, params);
    else
      this.routeProvider.openModal(this.routeProvider.pages.SelectPickerPage, params);
  }

  private async logDeliveryMethodSelectedEvent(objOrder) {
    const agentName = this.orderProvider.getPayoutAgentName(objOrder);
    const payoutAgentType = await this.orderProvider.getPayoutAgentType(objOrder);
    const pendingWalletType = this.getPendingWalletType();

    this.analytics.trackEvent({ eventName: AnalyticsEventType.DeliveryMethodSelected, analyticsData: {
      location: SegmentLocationNames.SendMoney,
      payoutAgentId: this.orderProvider.getPayoutAgentId(objOrder),
      payoutAgentName: agentName,
      payoutAgentType: payoutAgentType,
      payoutLocationId: objOrder.agentToLocation ? SegmentProvider.formatInt(objOrder.agentToLocation.locationId) : null,
      payoutLocationName: objOrder.agentToLocation ? objOrder.agentToLocation.locationName : null,
      payoutMethod: this.orderProvider.getAnalyticsDeliveryMethodType(objOrder.deliveryMethodInternal),
      recipientBankId: objOrder.recipient.bankId,
      recipientBankName: objOrder.recipient.bankName,
      pendingWalletType: pendingWalletType,
      pendingTransactionId: pendingWalletType && objOrder.orderNumber ? objOrder.orderNumber : null
    }});
  }

  logRecipientBankDetails(recipient) {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.RecipientBankDetailsAdded, analyticsData: {
      location: this.routeProvider.hasPage({page: SegmentLocationNames.Recipients}) ? SegmentLocationNames.Recipients : SegmentLocationNames.SendMoney,
      destinationCountry: recipient.country,
      payoutAgentId: recipient.bankId,
      payoutAgentName: this.orderProvider.getPayoutAgentName(this.orderProvider.getCurrentOrder()),
      recipientBankId: recipient.bankId,
      recipientBankName: recipient.bankName
    }}, true);
    this.analytics.flush();
  }

  async handleMobileWalletAndRiaWallet(order, newRecipient, replacePage, reselectingMobileWalletForSmartRouting = false) {
    // BE cannot send the Ria Wallet details due to limitations & so we have to handle it on FE by matching the name
    const walletsPromise = this.walletProvider.getMobileWallets(order);
    const wallets = await walletsPromise;
    if (!wallets || wallets.length === 0) {
      if (this.loading.isLoading())
        this.loading.hide();
      const corridor = `${order.countryTo} ${order.currencyFrom}-${order.currencyTo}`;
      const fetchError = this.walletProvider.walletFetchError;
      if (fetchError) {
        LoggerProvider.exception(this, fetchError, LoggerEvent.ProviderException, `Could not load mobile wallets for ${corridor}`);
        RmtPopupComponent.showExceptionWithLogTranslation(this, fetchError, 'global.errorGeneralMessage', 'global.errorGeneralHeader', null);
      } else {
        LoggerProvider.error(this, LoggerEvent.SendMoneyPage, `No mobile wallet providers available for ${corridor} - every provider is excluded`);
        this.orderProvider.showDeliveryMethodError(order, order.deliveryMethodInternal);
      }
      return;
    }
    if (wallets[0].EntityID)
      order.isSmartRouting = true;
    else if (wallets[0].WalletId && wallets[0].AggregatorId)
      order.isSmartRouting = false;
    this.orderProvider.setCurrentOrder(order);
    if (order.deliveryMethodAlias === DeliveryMethodType.RiaWallet) {
      // BE only sends Ria Wallet as a delivery method if it exists in the mobile wallet list, hence we assume that riaWalletDetails will always be there
      var riaWalletDetails;
      const riaWalletTarget = DeliveryMethodType.RiaWallet.toLowerCase().replace(/\s+/g, '');
      riaWalletDetails = wallets.find((wallet) => {
        // Smart-routing wallets have EntityName, non smart routing wallets have Name - only one is ever present
        const walletName = wallet.EntityName || wallet.Name;
        return walletName && walletName.toLowerCase().replace(/\s+/g, '') === riaWalletTarget;
      });

      // If Ria Wallet then directly render mobile wallet details page
      if (riaWalletDetails && riaWalletDetails.EntityID)
        this.selectWallet(order, riaWalletDetails.EntityID, newRecipient, true);
      else
        this.selectWallet(order, riaWalletDetails.WalletId, newRecipient, true);
    }

      if (!newRecipient && order.recipient && !reselectingMobileWalletForSmartRouting) {
        const trxs = this.customersProvider.getLastTransactions();
        if (trxs && order.deliveryMethodAlias !== DeliveryMethodType.RiaWallet) {
          const trx = trxs.find((trx) => (trx.BenNameId === order.recipient.id && trx.DelivMethod === DeliveryMethodType.MOBILE_WALLET_NUM_CODE))
          if (trx && (trx.PayAgentLocId || trx.SmartRoutingEntityId)) {
            const wallets = await walletsPromise;
            var matchedWallet
            matchedWallet = wallets.find((wallet) => ((trx.SmartRoutingEntityId && wallet.EntityID === trx.SmartRoutingEntityId) || (wallet.WalletId === trx.PayAgentLocId)));
            if (matchedWallet) {
              // unfortunately the backend is at the moment not full reliable to return the mobile wallet account number - that is being worked on already but in the meanwhile we have the workaround to take if mobile wallet account number of the last transaction if it's missing for the recipient
              if (!order.recipient.mobileWalletAccountNo) {
                const isLoading = this.loading.isLoading();
                if (!isLoading)
                  this.loading.show();
                try {
                  const status = await this.orderProvider.getOrderStatus(trx.OrderNo);
                  if (status && status.recipient && status.recipient.mobileWalletAccountNo) {
                    LoggerProvider.info(this, 'Reconstructed recipient mobile wallet account number from last order ' + trx.OrderNo);
                    order.recipient.mobileWalletAccountNo = status.recipient.mobileWalletAccountNo;
                  }
                } catch (ex) {
                  LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Unable to retrieve order number ' + trx.OrderNo + ' to get missing wallet account number');
                }
                if (!isLoading)
                  this.loading.hideDelayed();
              }
              this.selectWallet(order, matchedWallet.EntityID ? trx.SmartRoutingEntityId : trx.PayAgentLocId, newRecipient, false, order.recipient.mobileWalletAccountNo);
              return;
            }
          }
        }
      }

      if (order.deliveryMethodAlias !== DeliveryMethodType.RiaWallet) {
        // If mobile wallet then render mobile wallet list
        var pickerParams = this.walletProvider.getWalletSelectionParams(order.countryTo, walletsPromise);
        var onSelectCallback = (item) => {
          var id;
          if (item.EntityID)
            id = item.EntityID
          else
            id = item.WalletId
          this.selectWallet(order, id, newRecipient, true);
        };
        
        if (reselectingMobileWalletForSmartRouting) {
          this.routeProvider.openModal(this.routeProvider.pages.SelectPickerPage, {
            ...pickerParams,
            onSelect: onSelectCallback
          });
        }
        else
          this.routeProvider.pushOrReplace(replacePage, this.routeProvider.pages.SelectPickerPage, {
            ...pickerParams,
            onSelect: onSelectCallback
          });
      }
  }

  setPendingWalletType(walletType: string) {
    this.pendingWalletType = walletType;
  }

  getPendingWalletType() {
    return this.pendingWalletType;
  }

}



// WEBPACK FOOTER //
// ./src/providers/send-flow/send-flow.ts